import { useQuery } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { NodeCitation, NodeCitationsResponse } from "./use-get-functional-graph-service";

// Show at most this many citations in the UI. Capping here also keeps the
// request URL short — sending every ref builds a URL large enough to fail.
const CITATIONS_DISPLAY_LIMIT = 20;

// citationRefs can arrive either as a real array or as a JSON-encoded string
// like '["10844","10845"]'. Normalize to a string[] either way: a raw string
// has no .forEach, which previously threw before the fetch ran and made it look
// like the citations API "wasn't being called".

const getNodeCitations = async (
  ids: string[],
  projectUuid: string,
  headers: Record<string, string>
): Promise<NodeCitation[]> => {
  
  // Only ever request the first N citations, regardless of how many refs exist.
  const limitedIds = ids.slice(0, CITATIONS_DISPLAY_LIMIT);

  
  const params = new URLSearchParams();
  limitedIds.forEach((id) => params.append("filters[_id][$in]", id));
  params.append("filters[projectUuid][$eq]", projectUuid);
  params.append("selectFields", "name,type,reference,_id,projectUuid");

  const url = `${config.isometricApiUrl}/node-citations?${params.toString()}`;

  const response = await apiFetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to fetch node citations: ${response.statusText}`);
  }

  const result: NodeCitationsResponse = await response.json();
  return result.data;
};

const getAllProjectCitations = async (
  projectUuid: string,
  headers: Record<string, string>
): Promise<NodeCitation[]> => {
  const params = new URLSearchParams();
  params.append("filters[projectUuid][$eq]", projectUuid);
  params.append("selectFields", "name,type,reference,_id,projectUuid");

  const url = `${config.isometricApiUrl}/node-citations?${params.toString()}`;
  const response = await apiFetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to fetch project citations: ${response.statusText}`);
  }

  const result: NodeCitationsResponse = await response.json();
  return result.data;
};

export const useGetAllProjectCitations = (
  projectUuid: string | undefined
) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["all-project-citations", projectUuid],
    queryFn: () => {
      const headers = getAuthHeaders();
      return getAllProjectCitations(projectUuid!, headers);
    },
    enabled: !!projectUuid,
  });
};

export const useGetNodeCitations = (
  ids: string[] | string | undefined,
  projectUuid: string | undefined
) => {
  const { getAuthHeaders } = useAuthHeaders();

  // Normalize first so a JSON-string value still enables the query correctly.

  
  let normalizeIds: string[] = [];
  if (Array.isArray(ids)) {
    normalizeIds = ids;
  } else if (typeof ids === "string") {
    try {
      const parsed = JSON.parse(ids);
      if (Array.isArray(parsed)) normalizeIds = parsed.map((r) => String(r));
    } catch {
      normalizeIds = [];
    }
  }
  return useQuery({
    queryKey: ["node-citations", projectUuid, normalizeIds],
    queryFn: () => {
      const headers = getAuthHeaders();
      return getNodeCitations(normalizeIds, projectUuid!, headers);
    },
    enabled:Array.isArray(normalizeIds) &&  normalizeIds.length > 0 && !!projectUuid,
  });
};
